import { languages, type Language } from '@osteopro/shared';
import i18next, { type BackendModule, type ReadCallback, type i18n } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { syncZodLocale } from './zod-locale.js';

/**
 * i18next. Deux règles tenues de bout en bout :
 * 1. les clés sont **sémantiques** (`patients.list.title`), jamais des phrases françaises
 *    slugifiées comme en V1 (`common:general.passez_au_plan_professionnel_pour_acce…`) ;
 * 2. chaque espace de noms est un module chargé à la demande — les traductions ne pèsent pas
 *    sur le paquet principal.
 */

/**
 * `clinical` n'est pas un fichier de l'application : c'est le catalogue de `@osteopro/clinical`,
 * dont les clés sont dérivées des codes de tests, de critères et de questionnaires. Un fichier par
 * langue, chargé à la demande comme les autres espaces de noms.
 */
export const CLINICAL_NAMESPACE = 'clinical';

export const NAMESPACES = [
  'common',
  'shell',
  'auth',
  'patients',
  'settings',
  'agenda',
  'consultations',
  'pediatric',
  'documents',
  'dashboard',
  'billing',
  'landing',
  'legal',
  'feedback',
  'plans',
  'platform',
  'support',
  'audit',
  'onboarding',
  'help',
  CLINICAL_NAMESPACE,
] as const;
export type Namespace = (typeof NAMESPACES)[number];

export const LANGUAGE_STORAGE_KEY = 'osteopro.language';
/**
 * Préférence « défaut du cabinet » (page Profil, comme en V1). Distincte de la langue effective :
 * on mémorise le choix, et la langue appliquée suit `preferredLanguage` du cabinet actif.
 */
export const LANGUAGE_ORG_DEFAULT_KEY = 'osteopro.language.org-default';
export const DEFAULT_LANGUAGE: Language = 'fr';

function isLanguage(value: string | null): value is Language {
  return value !== null && (languages as readonly string[]).includes(value);
}

function storedLanguage(): Language | null {
  try {
    const value = globalThis.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return isLanguage(value) ? value : null;
  } catch {
    return null;
  }
}

/** Vrai quand l'utilisateur a choisi le défaut du cabinet plutôt qu'une langue fixe. */
export function isOrgDefaultLanguage(): boolean {
  try {
    return globalThis.localStorage.getItem(LANGUAGE_ORG_DEFAULT_KEY) === '1';
  } catch {
    return false;
  }
}

function setOrgDefaultLanguageFlag(enabled: boolean): void {
  try {
    if (enabled) {
      globalThis.localStorage.setItem(LANGUAGE_ORG_DEFAULT_KEY, '1');
      globalThis.localStorage.removeItem(LANGUAGE_STORAGE_KEY);
    } else {
      globalThis.localStorage.removeItem(LANGUAGE_ORG_DEFAULT_KEY);
    }
  } catch {
    // Stockage indisponible : le choix vaut pour la session.
  }
}

function navigatorLanguage(): Language | null {
  const candidates = [...globalThis.navigator.languages, globalThis.navigator.language];
  for (const candidate of candidates) {
    const short = candidate.slice(0, 2).toLowerCase();
    if (isLanguage(short)) return short;
  }
  return null;
}

/** Préférence explicite d'abord, langue du navigateur ensuite, français en dernier recours. */
export function detectLanguage(): Language {
  return storedLanguage() ?? navigatorLanguage() ?? DEFAULT_LANGUAGE;
}

/**
 * Ramène un code de langue quelconque — `de`, `de-DE`, `undefined` — à l'une des trois que le
 * produit parle.
 *
 * Ce que rend `i18n.language` n'est pas garanti nu : une préférence ancienne, un négociateur ou
 * une bascule peuvent y laisser une région, et le serveur n'accepte que `fr`, `de` ou `en`. Un
 * code inconnu vaut le repli plutôt qu'un refus de validation à l'envoi.
 */
export function toLanguage(value: string | undefined): Language {
  const short = (value ?? '').slice(0, 2).toLowerCase();
  return isLanguage(short) ? short : DEFAULT_LANGUAGE;
}

export function rememberLanguage(language: Language): void {
  setOrgDefaultLanguageFlag(false);
  try {
    globalThis.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Stockage indisponible (navigation privée) : la langue vaut pour la session, c'est tout.
  }
}

/** Mémorise le choix « défaut du cabinet » et applique la langue effective fournie. */
export function rememberOrgDefaultLanguage(): void {
  setOrgDefaultLanguageFlag(true);
}

/**
 * Les trois fichiers du catalogue clinique, énumérés. Un `import()` paramétré sur un chemin de
 * paquet ne se résout pas en modules distincts : le regroupement resterait au hasard du bundler,
 * et tout le catalogue finirait dans le paquet principal.
 */
const CLINICAL_LOCALES: Readonly<
  Record<string, () => Promise<{ default: Record<string, unknown> }>>
> = {
  // Sans l'extension : la carte `exports` du paquet mappe `./locales/*` sur `./src/locales/*.json`.
  fr: () => import('@osteopro/clinical/locales/fr'),
  de: () => import('@osteopro/clinical/locales/de'),
  en: () => import('@osteopro/clinical/locales/en'),
};

function loadClinicalLocale(language: string): Promise<{ default: Record<string, unknown> }> {
  const load = CLINICAL_LOCALES[language] ?? CLINICAL_LOCALES[DEFAULT_LANGUAGE];
  return load === undefined
    ? Promise.reject(new Error(`Catalogue clinique absent pour ${language}`))
    : load();
}

/**
 * Chargement paresseux : Vite transforme l'`import()` paramétré ci-dessous en un module par
 * fichier de traduction, ce qui sort les trois langues du paquet principal.
 */
const lazyBackend: BackendModule = {
  type: 'backend',
  init: () => undefined,
  read: (language: string, namespace: string, callback: ReadCallback) => {
    const load =
      namespace === CLINICAL_NAMESPACE
        ? loadClinicalLocale(language)
        : import(`../locales/${language}/${namespace}.json`);
    load
      .then((module: { default: Record<string, unknown> }) => {
        callback(null, module.default);
      })
      .catch((error: unknown) => {
        callback(error instanceof Error ? error : new Error(String(error)), false);
      });
  },
};

let instance: i18n | null = null;

export function getI18n(): i18n {
  instance ??= createI18n();
  return instance;
}

function createI18n(): i18n {
  const created = i18next.createInstance();
  void created
    .use(lazyBackend)
    .use(initReactI18next)
    .init({
      lng: detectLanguage(),
      fallbackLng: DEFAULT_LANGUAGE,
      supportedLngs: [...languages],
      ns: ['common'],
      defaultNS: 'common',
      partialBundledLanguages: true,
      interpolation: { escapeValue: false },
      returnNull: false,
      // `I18nProvider` attend explicitement le chargement des espaces de noms. Laisser en plus
      // chaque `useTranslation` suspendre viderait l'écran sans limite de temps.
      react: { useSuspense: false },
    });
  // Les messages de validation zod suivent la même langue que l'interface, dès le démarrage.
  syncZodLocale(created);
  return created;
}
